import { SeoEnricherSection } from '../utils/seoEnricherPreview';

export const CITY_SEO_ENRICHED_SECTIONS: SeoEnricherSection[] = [
  {
    intent: 'booking_process',
    title: 'Booking Your Cab in ${city_name} Made Easy',
    subtitle: 'A simple guide to booking your cab in ${city_name}',
    content:
      '<p>Booking your cab with MakeMyTrip in ${city_name} is straightforward and user-friendly. You can choose from various trip types, including one-way, round-trip, and multi-city bookings, making it easy to plan your travel according to your needs.</p><table><thead><tr><th>Trip Type</th><th>Description</th><th>How to Book</th></tr></thead><tbody><tr><td>One-Way</td><td>Travel from ${city_name} to your destination without a return trip.</td><td>Select the one-way option during booking.</td></tr><tr><td>Round-Trip</td><td>Travel from ${city_name} to your destination and back.</td><td>Choose the round-trip option and specify your return details.</td></tr><tr><td>Multi-City</td><td>Visit multiple destinations starting from ${city_name} in one booking.</td><td>Use the multi-city option to add stops during your journey.</td></tr></tbody></table><p>Make sure to check the available payment options, which include credit cards, debit cards, net banking, and digital wallets. You can also modify your booking details for free up to one hour before your scheduled departure.</p><p>For promotional discounts, apply the coupon code <strong>CABTRIP</strong> during the booking process to enjoy savings on your fare.</p>',
    faqs: [
      {
        question: 'How can I book a round-trip cab from ${city_name}?',
        answer:
          'To book a round-trip cab from ${city_name}, simply select the round-trip option during the booking process and enter your destination, departure, and return details. MakeMyTrip makes it easy to manage your travel plans.'
      },
      {
        question:
          'What payment options are available when booking a cab from ${city_name}?',
        answer:
          'When booking a cab from ${city_name}, you can choose from various payment options including credit cards, debit cards, net banking, and digital wallets. MakeMyTrip provides flexible payment methods to suit your preferences.'
      },
      {
        question: 'Can I modify my cab booking from ${city_name}?',
        answer:
          'Yes, you can request free modifications to your travel dates, pickup/drop locations, and vehicle types up to one hour before your departure.'
      }
    ]
  },
  {
    intent: 'sightseeing_tourism',
    title: 'Sightseeing and Cab Tours from ${city_name}',
    subtitle:
      'Explore sightseeing options when booking a cab from ${city_name}',
    content:
      '<p>When you book a round-trip cab with MakeMyTrip from ${city_name}, sightseeing is included within the designated kilometer limits at your destination. You can also request local sightseeing at your source during the review stage of your booking.</p><p>For one-way trips from ${city_name}, sightseeing is not permitted as these are direct transfers. Any custom stops must be explicitly added at the time of booking.</p>',
    faqs: [
      {
        question: 'Can I book a cab for sightseeing from ${city_name}?',
        answer:
          'Yes, when you book a round-trip cab with MakeMyTrip from ${city_name}, sightseeing is included within the designated kilometer limits at your destination.'
      },
      {
        question:
          'Can I stop for sightseeing during a one-way cab journey from ${city_name}?',
        answer:
          'No, sightseeing is not allowed during one-way trips from ${city_name}, as these are direct transfers. You can add required stops by choosing an applicable multi-city cab option.'
      },
      {
        question:
          'Will the driver take us sightseeing during a round trip from ${city_name}?',
        answer:
          'Yes, during your round trip with MakeMyTrip, the driver can take you for sightseeing at your destination, provided it fits within the designated kilometer limits.'
      }
    ]
  },
  {
    intent: 'cab_type_selection',
    title: 'Choosing the Right Cab in ${city_name}',
    subtitle: 'Find the right cab for your journey from ${city_name}',
    content:
      '<p>When planning your journey from ${city_name}, selecting the right cab type is essential to ensure a comfortable and convenient travel experience. MakeMyTrip Cabs offers a variety of options tailored to different group sizes and luggage needs.</p><table><thead><tr><th>Cab Type</th><th>Seat Capacity</th><th>Fuel Types Available</th><th>Boot Space (Bags)</th></tr></thead><tbody><tr><td>Hatchback</td><td>4</td><td>Diesel</td><td>2</td></tr><tr><td>Sedan</td><td>4</td><td>CNG, Diesel, Electric</td><td>2–3</td></tr><tr><td>SUV</td><td>6</td><td>CNG, Diesel</td><td>2–3</td></tr><tr><td>Compact SUV</td><td>4</td><td>Electric</td><td>4</td></tr><tr><td>Electric SUV</td><td>4</td><td>Electric</td><td>4</td></tr><tr><td>Tempo Traveller</td><td>12</td><td>Diesel</td><td>8</td></tr></tbody></table><p><strong>Note: Roof carrier availability may vary by cab type, so please check the cab card during booking for specific options.</strong></p>',
    faqs: [
      {
        question: 'Which cab should I choose for my trip from ${city_name}?',
        answer:
          'The best cab for your trip from ${city_name} depends on your group size and luggage needs. MakeMyTrip offers various options, including Hatchbacks, Sedans, SUVs, and more, to accommodate different requirements.'
      },
      {
        question:
          'Are there suitable cab options for 4 passengers traveling from ${city_name}?',
        answer:
          'Yes, MakeMyTrip has a range of cab options suitable for 4 passengers traveling from ${city_name}. You can choose from Hatchbacks, Sedans, SUVs, or other available options based on your requirements.'
      }
    ]
  },
  {
    intent: 'fare_inclusions',
    title: 'What’s Included in Your Cab Fare',
    subtitle:
      'Understand the components of your cab fare when travelling from ${city_name}',
    content:
      "<p>When you book a cab with MakeMyTrip for your journey from ${city_name}, several components are included in your fare. This ensures transparency and helps you understand what you are paying for.</p><table><thead><tr><th>Fare Component</th><th>What It Covers</th><th>When It Applies</th></tr></thead><tbody><tr><td>Toll Charges</td><td>All applicable toll charges for your route</td><td>Included in the fare for one-way and round trips</td></tr><tr><td>Parking Fees</td><td>Parking fees at the pickup and drop-off locations</td><td>Included in the fare</td></tr><tr><td>Driver Allowance</td><td>Driver's allowance for meals and accommodation</td><td>Applicable for round trips</td></tr></tbody></table><p>For additional services, you can opt for extras such as expressway route selection, driver language preference, and roof carriers for extra luggage capacity, each available for a nominal fee.</p>",
    faqs: [
      {
        question:
          'Are toll charges included in the fare for a one-way cab from ${city_name}?',
        answer:
          'Yes, toll charges are included in the fare for your one-way cab journey from ${city_name} with MakeMyTrip.'
      },
      {
        question:
          'What is included in the total fare for a round-trip cab from ${city_name}?',
        answer:
          'The total fare for your round-trip cab from ${city_name} with MakeMyTrip includes toll charges, parking fees, and driver allowances.'
      },
      {
        question:
          'Are parking fees included when booking a cab from ${city_name}?',
        answer:
          'Yes, parking fees are included in the fare for your cab booking from ${city_name} with MakeMyTrip.'
      }
    ]
  },
  {
    intent: 'waiting_time_policy',
    title: 'Cab Waiting Time Policies',
    subtitle:
      'Understand waiting time policies for your cab booking from ${city_name}',
    content:
      '<p>At MakeMyTrip, we strive to provide clear and transparent policies regarding waiting times for your cab booking. Understanding these policies can help you plan your journey from ${city_name} effectively.</p><table><thead><tr><th>Policy Area</th><th>Details</th></tr></thead><tbody><tr><td>Free Waiting at Pickup</td><td>A complimentary free waiting window of 30 to 45 minutes is provided at pickup, depending on the cab category selected.</td></tr><tr><td>Charges After Grace Period</td><td>Beyond the free grace period, applicable waiting charges accrue in 30 to 60 minute slabs based on vehicle type, driver availability, and vendor fare rules.</td></tr><tr><td>No-Charge Waiting at En-Route Stops</td><td>Waiting at en-route stops does not incur additional charges, allowing you to take breaks without worry.</td></tr></tbody></table><p>Note: Prolonged pickup delays exceeding the maximum allowed waiting limit may result in driver release and trip cancellation without refund.</p>',
    faqs: [
      {
        question:
          'Will the cab wait if I am delayed at pickup in ${city_name}?',
        answer:
          'Yes, MakeMyTrip provides a complimentary waiting window of 30 to 45 minutes at pickup, depending on the cab category selected.'
      },
      {
        question:
          'Are there waiting charges for cab bookings from ${city_name}?',
        answer:
          "Yes, waiting charges may apply beyond the free waiting period, accruing in 30 to 60 minute slabs based on vehicle type and driver availability, as per MakeMyTrip's policies."
      },
      {
        question:
          'Is there a time limit for rest stops during my journey from ${city_name}?',
        answer:
          'While there is no specific time limit for rest stops, MakeMyTrip encourages you to be mindful of the overall trip duration to avoid any additional waiting charges.'
      }
    ]
  },
  {
    intent: 'driver_quality',
    title: 'Driver Quality and Professionalism',
    subtitle:
      'Know what to expect from your cab driver when travelling from ${city_name}',
    content:
      '<p>When you book a cab from ${city_name} with MakeMyTrip Cabs, you can expect a high standard of driver quality and professionalism. Your driver and vehicle details will be shared up to 30 minutes before your scheduled departure time, allowing you to verify the information before boarding.</p><table><thead><tr><th>Policy Area</th><th>Details</th></tr></thead><tbody><tr><td>Driver Allowance</td><td>Driver allowance for food and accommodation is included in the fare, and drivers make their own arrangements, so no extra payment is required.</td></tr><tr><td>Driver Verification</td><td>Verify your vehicle details before boarding; if a different cab or driver arrives, do not board and contact customer support immediately for assistance.</td></tr><tr><td>Language Preference</td><td>While booking, you can request a driver who speaks your preferred language for better communication.</td></tr></tbody></table><p>MakeMyTrip is committed to providing verified drivers, ensuring a reliable and professional travel experience for passengers travelling from ${city_name}.</p>',
    faqs: [
      {
        question:
          'Are driver food and accommodation expenses included when booking an outstation cab from ${city_name}?',
        answer:
          'Yes, outstation cab drivers have their food and accommodation expenses covered as part of the fare, so no extra payment is required for these arrangements when you travel with MakeMyTrip.'
      },
      {
        question:
          'Can I request an English-speaking driver for my cab from ${city_name}?',
        answer:
          'Yes, you can request a driver who speaks English when booking your cab with MakeMyTrip, ensuring better communication during your journey.'
      },
      {
        question:
          'Will the same driver stay with me throughout my round trip from ${city_name}?',
        answer:
          'Yes, when you book a round-trip cab with MakeMyTrip, the same driver will stay with you throughout the entire journey.'
      }
    ]
  },
  {
    intent: 'night_travel',
    title: 'Night Travel Charges and Policies',
    subtitle: 'Understand night charges when booking a cab from ${city_name}',
    content:
      "<p>When you book a cab with MakeMyTrip for travel from ${city_name}, it's important to be aware of the night travel charges that may apply. Night charges are applicable for cab bookings when the vehicle is used between 10:00 PM and 6:00 AM. These charges account for overnight driver allowances and the operational costs associated with late-hour travel.</p><table><thead><tr><th>Policy Area</th><th>Details</th></tr></thead><tbody><tr><td>Night Charge Timing</td><td>Applicable from 10:00 PM to 6:00 AM</td></tr><tr><td>Applicability</td><td>Round trip and multi-city bookings incur extra charges</td></tr><tr><td>Payment Method</td><td>Included in fare for one-way; paid directly to driver for round trips</td></tr></tbody></table><p>For more detailed information regarding specific rates, please refer to the final trip selection during your booking process.</p>",
    faqs: [
      {
        question:
          'What are the night charges for cab bookings from ${city_name}?',
        answer:
          "Night charges apply when the vehicle is used between 10:00 PM and 6:00 AM. These charges are included in the overall fare for one-way trips. For round trips, additional fees will be paid directly to the driver, so it's best to check the details during your booking with MakeMyTrip."
      },
      {
        question:
          'When are night charges applicable for cabs from ${city_name}?',
        answer:
          'Night charges are applicable for cab bookings from ${city_name} when the travel occurs between 10:00 PM and 6:00 AM. MakeMyTrip ensures that you are informed about these charges during your booking process.'
      }
    ]
  },
  {
    intent: 'group_travel',
    title: 'Group Cab Travel from ${city_name}',
    subtitle: 'Explore cab options for group travel from ${city_name}',
    content:
      "<p>When planning a group trip from ${city_name}, MakeMyTrip Cabs offers a variety of options to accommodate your needs. Whether you're traveling with family, friends, or colleagues, available cab options are designed to provide comfort and convenience for larger groups.</p><table><thead><tr><th>Cab Type</th><th>Seat Capacity</th><th>Fuel Types Available</th><th>Boot Space (Bags)</th></tr></thead><tbody><tr><td>SUV</td><td>6</td><td>CNG, Diesel</td><td>2–3</td></tr><tr><td>Tempo Traveller</td><td>12</td><td>Diesel</td><td>8</td></tr></tbody></table><p>Each vehicle type comes with standard inclusions such as state taxes, parking fees, and a uniformed driver to ensure a hassle-free experience. For larger groups, the Tempo Traveller is particularly suitable, offering ample seating and luggage capacity.</p>",
    faqs: [
      {
        question:
          'What cab options are available for a group of 12 travelers from ${city_name}?',
        answer:
          'For a group of 12 travelers, MakeMyTrip recommends booking a Tempo Traveller, which comfortably accommodates your group while providing sufficient luggage space.'
      },
      {
        question: 'Can I book an SUV for 6 adults traveling from ${city_name}?',
        answer:
          'Yes, you can book an SUV through MakeMyTrip Cabs for 6 adults, ensuring a comfortable journey with enough space for luggage.'
      },
      {
        question:
          'What is the luggage capacity of a Tempo Traveller when travelling from ${city_name}?',
        answer:
          'The Tempo Traveller can accommodate up to 8 bags, making it an excellent choice for group travel with substantial luggage.'
      }
    ]
  }
];
